import { noop } from 'lodash';
import { useMutation, useQueryClient } from 'react-query';

import {
  NEW_VIEW,
  NEW_VIEW_TEMPLATE_NAME
} from '@/constants/dashboardConstants';
import { ProjectTemplate } from '@experiment-management-shared/types';
import { projectViewsStore } from '@shared';
import api from '../api';
import useProject from './useProject';

const removeView = async ({
  canEdit,
  personalViewId,
  projectId,
  viewId
}: {
  canEdit: boolean;
  personalViewId?: string | null;
  projectId: string;
  viewId?: string;
}) => {
  const localViewId = viewId ?? NEW_VIEW;
  const hasLocalView = await projectViewsStore
    .get({
      projectId,
      viewId: localViewId
    })
    .catch(() => false);

  if (hasLocalView) {
    await projectViewsStore.delete([projectId, localViewId]).catch(noop);
  }

  if (!canEdit) return;

  return api.post('dashboard-templates/project/delete', {
    project_id: projectId,
    template_id: personalViewId ?? viewId
  });
};

export default function useRemoveViewMutation() {
  const { data: project } = useProject();
  const queryClient = useQueryClient();
  const projectId = project?.projectId;

  return useMutation(
    ({
      personalViewId,
      projectId: viewProjectId = projectId,
      viewId
    }: {
      isDefaultView?: boolean;
      personalViewId?: string | null;
      projectId?: string;
      viewId?: string;
    }) => {
      return removeView({
        canEdit: project?.canEdit ?? false,
        personalViewId,
        projectId: viewProjectId,
        viewId
      });
    },
    {
      onMutate: async () => {
        await queryClient.cancelQueries(['projectViews', { projectId }]);
      },
      onSuccess: async (data, { isDefaultView, personalViewId, viewId }) => {
        const deletedViewId = personalViewId ?? viewId;

        queryClient.setQueryData<ProjectTemplate[] | undefined>(
          ['projectViews', { projectId }],
          templates => {
            if (!templates) return templates;

            return templates
              .filter(template => template.templateId !== deletedViewId)
              .map(template => {
                if (
                  template.templateName !== NEW_VIEW_TEMPLATE_NAME ||
                  !isDefaultView
                ) {
                  return template;
                }

                // if deleted view was default view, replace default project
                return { ...template, projectDefault: true };
              });
          }
        );

        queryClient.invalidateQueries(['projectViews', { projectId }]);
      }
    }
  );
}
